﻿import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FileText, Search } from "lucide-react";

import type { Order } from "@/components/KanbanCard";
import { QuotePDFModal } from "@/components/QuotePDFModal";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/lib/CompanyProvider";
import {
  useQuotesData,
  isPending,
  isCompleted,
  isInProgress,
} from "@/hooks/quotes/useQuotesData";
import { QuotesFilters } from "@/components/quotes/QuotesFilters";
import { QuotesList } from "@/components/quotes/QuotesList";

export const Route = createFileRoute("/app/quotes")({
  component: QuotesPage,
});

function QuotesPage() {
  const { activeCompanyId } = useCompany();

  const [selectedQuote, setSelectedQuote] = useState<Order | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const {
    quotes,
    isLoading,
    searchTerm,
    setSearchTerm,
    statusTab,
    setStatusTab,
    currentPage,
    setCurrentPage,
    ITEMS_PER_PAGE,
    convertToOrder,
    deleteQuote,
  } = useQuotesData(activeCompanyId);

  const pendingCount = quotes.filter(isPending).length;
  const inProgressCount = quotes.filter(isInProgress).length;
  const completedCount = quotes.filter(isCompleted).length;
  const allCount = quotes.length;

  const filteredQuotes = quotes
    .filter((q) => {
      if (statusTab === "PENDING") return isPending(q);
      if (statusTab === "IN_PROGRESS") return isInProgress(q);
      if (statusTab === "COMPLETED") return isCompleted(q);
      return true;
    })
    .filter(
      (q) =>
        q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.vehicle_model?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

  const totalPages = Math.ceil(filteredQuotes.length / ITEMS_PER_PAGE) || 1;
  const paginatedQuotes = filteredQuotes.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-100 p-6 overflow-y-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
            <FileText className="text-blue-500" />
            Gestão de Orçamentos
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Gere propostas comerciais, envie pelo WhatsApp e converta em Ordem
            de Serviço com 1 clique.
          </p>
        </div>

        <div className="relative w-full md:w-72">
          <Search
            className="absolute left-3 top-2.5 text-slate-500"
            size={18}
          />
          <input
            type="text"
            placeholder="Buscar cliente, placa ou carro..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <QuotesFilters
        statusTab={statusTab}
        setStatusTab={setStatusTab}
        setCurrentPage={setCurrentPage}
        allCount={allCount}
        pendingCount={pendingCount}
        inProgressCount={inProgressCount}
        completedCount={completedCount}
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center text-slate-500">
          Carregando orçamentos...
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-12 bg-slate-900/40 border border-slate-800/80 rounded-2xl text-center">
          <FileText className="w-12 h-12 text-slate-600 mb-4" />
          <h3 className="text-lg font-bold text-slate-300">
            Nenhum orçamento encontrado
          </h3>
          <p className="text-slate-500 text-sm max-w-sm mt-1">
            {statusTab === "PENDING"
              ? "Não há orçamentos pendentes de aprovação no momento."
              : statusTab === "IN_PROGRESS"
                ? "Não há orçamentos em andamento no pátio."
                : statusTab === "COMPLETED"
                  ? "Nenhum orçamento finalizado no histórico ainda."
                  : 'Crie um orçamento clicando no botão "+ Nova OS" na Recepção e selecionando "Gerar Orçamento".'}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-between space-y-6">
          <QuotesList
            paginatedQuotes={paginatedQuotes}
            deleteQuote={deleteQuote}
            convertToOrder={convertToOrder}
            setSelectedQuote={setSelectedQuote}
            setPdfModalOpen={setPdfModalOpen}
          />

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-slate-800 text-xs text-slate-400">
              <span>
                Mostrando página {currentPage} de {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev: number) => Math.max(prev - 1, 1))
                  }
                  className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-300"
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev: number) =>
                      Math.min(prev + 1, totalPages),
                    )
                  }
                  className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-300"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <QuotePDFModal
        order={selectedQuote}
        open={pdfModalOpen}
        onOpenChange={setPdfModalOpen}
      />
    </div>
  );
}
